import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useSnackbar } from '@/context/SnackbarProvider';
import { retryHauserEQEvaluation } from '@/services/api/hauser-eq/api';

/**
 * Manually retry a HauserEQ pipeline whose auto-retry (hauser-eq's DLQ
 * reprocessor, see #16/#20) has already been exhausted and landed on
 * context_status/condition_status: 'failed'. Mirrors
 * useTriggerRecalculation's toast + invalidate convention, but goes through
 * a separate Advisor+Admin-accessible endpoint (not the Admin-only
 * /admin/properties/{id}/recalculate) since this is meant to live directly
 * on the score card as a response to a visible failure, not as an ops tool.
 *
 * Invalidates rather than assuming completion — the retry only re-queues
 * the pipeline, it doesn't score synchronously. useHauserEQResults' own
 * refetchInterval picks up polling again once the response shows 'retrying'.
 */
export const useRetryHauserEQEvaluation = () => {
  const { showAlert } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ propertyId, scoreType }: { propertyId: string; scoreType: 'home' | 'area' }) =>
      retryHauserEQEvaluation(propertyId, scoreType),
    onSuccess: (_data, { propertyId, scoreType }) => {
      queryClient.invalidateQueries({ queryKey: ['hauser-eq-results', propertyId] });
      showAlert(
        `Retrying ${scoreType === 'home' ? 'Home' : 'Area'} score — may take a few minutes`,
        'info',
      );
    },
    onError: (_error, { scoreType }) => {
      showAlert(`Failed to retry ${scoreType === 'home' ? 'Home' : 'Area'} score`, 'error');
    },
  });
};
